import type { PlaybackState, VocalChannel } from '../../api/types.ts';
import { ApiError } from '../../api/client.ts';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import type { MessageKey } from '../../i18n/messages.en.ts';
import type { StageSyncPayload } from './stageChannel.ts';

/**
 * Pure clamping/cycling rules behind the cockpit's transport strip and the AC-OPS-5 hotkeys
 * (key ±1 semitone, sync offset ∓10ms, vocal-channel cycle, mute). Split out from the React glue
 * so every rule is testable without mounting anything — the schema-side clamps
 * (`strapi-template/src/utils/playback-state.js`) are the source of truth for the *ranges*; these
 * mirror them so the UI never optimistically shows a value the server would have clamped anyway.
 */

export const KEY_SHIFT_MIN = -12;
export const KEY_SHIFT_MAX = 12;
export const TEMPO_PCT_MIN = -20;
export const TEMPO_PCT_MAX = 20;
export const LYRIC_OFFSET_MIN_MS = -2000;
export const LYRIC_OFFSET_MAX_MS = 2000;
export const VOLUME_MIN = 0;
export const VOLUME_MAX = 100;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clampKeyShift(value: number): number {
  return clamp(Math.round(value), KEY_SHIFT_MIN, KEY_SHIFT_MAX);
}

export function clampTempoPct(value: number): number {
  return clamp(Math.round(value), TEMPO_PCT_MIN, TEMPO_PCT_MAX);
}

export function clampLyricOffsetMs(value: number): number {
  return clamp(Math.round(value), LYRIC_OFFSET_MIN_MS, LYRIC_OFFSET_MAX_MS);
}

export function clampVolume(value: number): number {
  return clamp(Math.round(value), VOLUME_MIN, VOLUME_MAX);
}

/** `M` mute (AC-OPS-5): toggles between 0 and the last non-zero volume, so un-muting restores the
 * operator's actual level rather than snapping to a fixed 100. */
export function toggleMuteVolume(currentVolume: number, lastNonZeroVolume: number): number {
  if (currentVolume > 0) return 0;
  return lastNonZeroVolume > 0 ? lastNonZeroVolume : VOLUME_MAX;
}

const VOCAL_CHANNEL_CYCLE: VocalChannel[] = ['both', 'left', 'right', 'none'];

/** Cycles Off/Left/Right/Both — `unknown` (never explicitly set) enters the cycle at `both`
 * (the most common source layout) rather than being a dead end a click can never leave. */
export function cycleVocalChannel(current: VocalChannel): VocalChannel {
  const index = VOCAL_CHANNEL_CYCLE.indexOf(current);
  if (index === -1) return VOCAL_CHANNEL_CYCLE[0];
  return VOCAL_CHANNEL_CYCLE[(index + 1) % VOCAL_CHANNEL_CYCLE.length];
}

/** The patch body for loading a NEW song without yet claiming it is playing (P2 fix, round 5) —
 * shared by App.tsx's ad-hoc "Play now" and OperatorConsole.tsx's "Start next"/rotation "Play
 * now", so the "never claim `isPlaying` before a real `play` event" rule lives in exactly one
 * place. Both call sites used to write `mode:'playing', isPlaying:true` here even though nothing
 * had actually started decoding — confirmed live: the Stage window took that as truth and ran a
 * phantom clock off a video that never played. The real handoff to `'playing'` happens only from
 * `usePlaybackEngine.ts`'s existing `onPlay` handler, once an actual `play` event fires.
 *
 * R13-P2-D fix (round 13, live-Chrome critic): this used to write `mode:'paused'` as "the closest
 * honest value available" — but every song start therefore wrote a transient `{isPlaying:false,
 * mode:'paused'}` cue ~300ms before the real `{mode:'playing'}` landed, and every connected client
 * (the Stage's `shouldShowPausedBadge`, the rotation `QueuePanel` row) read that as a genuine pause
 * and flashed paused UI on every single song change. `'loading'` (now in the `playback-state` mode
 * enum, see schema.json's own note) is the honest value: a song is attached and about to play, but
 * nothing has confirmed it yet — neither "paused" (implies a deliberate stop) nor "playing" (not
 * true yet). `stageScene.ts`'s `pickStageScene`/`shouldShowPausedBadge` and `rotationSnapshot.ts`'s
 * `slotBadge` both treat `'loading'` distinctly from `'paused'`. */
export function cuedSongPatch(
  keyShift: number
): { mode: 'loading'; isPlaying: false; positionMs: 0; keyShift: number; errorCode: null; errorMessage: null } {
  // R8-P1-3 fix (round 8, live-Chrome critic): a stale `errorCode`/`errorMessage` from an earlier
  // failed song used to survive every later song change — only `advanceToNext`'s own internal
  // fallback patch (usePlaybackEngine.ts, used when no `onAdvance` is wired up) ever cleared them,
  // so `OperatorConsole.tsx`'s shared `advance()` and App.tsx's ad-hoc Library "Play now" (both of
  // which build their patch body from THIS function) left the Stage's error hold card
  // (`stageScene.ts`'s `hasError` priority) showing "Couldn't play X" over every later song, even
  // while they played fine. Every song change is, definitionally, a fresh chance to succeed.
  return { mode: 'loading', isPlaying: false, positionMs: 0, keyShift, errorCode: null, errorMessage: null };
}

/**
 * R9-P1-A fix (round 9, live-Chrome critic): any write that changes `positionMs` or `isPlaying`
 * must also stamp a fresh `positionAtServerMs`, or `extrapolatePositionMs` (clock.ts) keeps
 * extrapolating forward from whatever ancient timestamp happened to still be on the row —
 * reproduced live as "Play now" landing `positionMs:0, isPlaying:true` with `positionAtServerMs`
 * left over from a pause written 2.5 minutes earlier: the Stage extrapolated straight to
 * end-of-song and flickered there for 4s until the next periodic re-anchor. Several call sites
 * across `usePlaybackEngine.ts`/`OperatorConsole.tsx` already remember to pass a fresh
 * `Date.now()` alongside `positionMs`/`isPlaying`, and several don't (`onPlay`, `onPause`,
 * `togglePlay`'s catch branch, `onEmptied`, the stall watchdog's error patch is fine since it
 * touches neither field) — rather than auditing every call site forever, `usePlaybackState.ts`'s
 * `patch()` runs every outgoing body through this first, so the rule holds even for a call site
 * added tomorrow. Never overwrites a stamp the caller explicitly supplied (a caller with the
 * ELEMENT's real position in hand, e.g. a resume, knows better than "right now").
 */
export function withPositionStamp<T extends object>(patch: T, nowMs: number = Date.now()): T {
  // Narrowed via a cast, not a generic constraint: constraining `T` structurally to
  // `PlaybackPatchLike` would force every CALLER's own type (`api/playback.ts`'s `PlaybackPatch`,
  // which has no index signature) to satisfy this module's shape rather than the other way round —
  // TS then refuses the call ("index signature missing"). `T extends object` accepts anything;
  // the cast below is where the actual field check happens.
  const fields = patch as PlaybackPatchLike;
  const touchesPosition = 'positionMs' in fields || 'isPlaying' in fields;
  if (!touchesPosition || 'positionAtServerMs' in fields) return patch;
  // R11-P2-4 fix (round 11, live-Chrome critic): `nowMs` (usually `serverNowMs`'s output below) can
  // be fractional after averaging a clock offset against a round-trip midpoint — critic caught
  // `positionAtServerMs:1788421521975.5` landing on the wire. The column is a Strapi `biginteger`,
  // wire-serialised as a JSON STRING (`toEpochMs`'s own doc comment) — a fractional value there is
  // not just cosmetic, it is bytes the server has to store and every client has to re-parse.
  // Rounding at the one place every write funnels through (this function) fixes every call site.
  return { ...patch, positionAtServerMs: Math.round(nowMs) };
}

/** The minimal shape `withPositionStamp` reads off its argument via a cast (see that function's
 * own doc comment for why this is not a generic constraint). */
interface PlaybackPatchLike {
  positionMs?: number;
  isPlaying?: boolean;
  positionAtServerMs?: number;
}

/**
 * P0 fix (round 6): every playback-state write (`usePlaybackState.ts`'s `patch`) used to let a
 * failed `PATCH /api/playback/state` become an unhandled promise rejection at every fire-and-forget
 * `void patch(...)` call site in `usePlaybackEngine.ts` — confirmed live as the stall-watchdog loop
 * (see that file's own doc comment) hammered the 4/s server-side rate limit into 96 `429`s in under
 * 3 minutes, each one surfacing as a raw console exception instead of readable copy. Mirrors
 * `features/guest/requestErrors.ts`'s own `retryAfterOf` — same wire shape
 * (`details.retryAfterSec`), independent copy since ops-dualscreen's cockpit and the guest request
 * flow have no shared UI component to route through.
 */
export function retryAfterSecFromApiError(error: unknown): number | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const raw = error.details?.retryAfterSec;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : undefined;
}

/** `true` only for the server's own rate limit (429) — a 401/404/5xx wants its own distinct copy,
 * not "you're going too fast". */
export function isRateLimitedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 429;
}

/** Turns any `patch()` failure into cockpit-ready copy: a rate limit names how long to wait, every
 * other failure just states what happened. Kept pure/testable — the call site (`usePlaybackState.ts`)
 * only has to decide WHEN to show it.
 *
 * MVP-389: `t` is a parameter for the same reason `api/errorMessage.ts`'s `fallback` is one — this
 * is a plain function with no component around it, and the three sentences it used to build by hand
 * were English on a Thai-default UI, in the cockpit's own error banner. `error.message` went with
 * them: for an `ApiError` that is whatever the wire said (MVP-371's whole argument), and for a
 * `TypeError` from a dropped fetch it is the browser's English. */
export function describeControlWriteError(
  error: unknown,
  t: (key: MessageKey, params?: Record<string, string | number>) => string
): { message: string; retryAfterSec?: number } {
  const retryAfterSec = retryAfterSecFromApiError(error);
  if (isRateLimitedError(error)) {
    return {
      message: retryAfterSec
        ? t('ops.error.rateLimitedRetry', { seconds: retryAfterSec })
        : t('ops.error.rateLimited'),
      retryAfterSec,
    };
  }
  return { message: apiErrorMessage(error, t('ops.error.controlWriteFailed'), t) };
}

/**
 * P1-1 fix (round 7, live-Chrome critic): guards `OperatorConsole.tsx`'s shared `advance()`
 * sequencing against a doubled call — reproduced live as an auto-advance that skipped a queue
 * entry entirely because the prefetched/back `<video>` element (already sitting at `ended` on the
 * same `src` as the front one) fired a SECOND `ended` event that re-entered `advanceToNext` before
 * `activeSong` had caught up to the first advance. Either check alone is enough to skip:
 *  1. `advanceInFlight` — a second call must never race a first one that hasn't finished writing
 *     playback-state/queue statuses yet.
 *  2. `callerCurrentEntryId !== playingEntryId` — the queue entry the CALLER believes is currently
 *     playing no longer matches what OperatorConsole's own read model says is playing, which means
 *     this is a stale/duplicate call arriving after a legitimate advance already moved on.
 */
export function shouldSkipAdvance(params: { advanceInFlight: boolean; callerCurrentEntryId: string | null; playingEntryId: string | null }): boolean {
  if (params.advanceInFlight) return true;
  return params.callerCurrentEntryId !== params.playingEntryId;
}

/** Coerces the wire value of a Strapi `biginteger` column (a JSON string, to dodge float
 * precision loss on epoch-ms — see `api/types.ts`'s `PlaybackState.positionAtServerMs` doc) to
 * the `number | null` every pure consumer (`clock.ts`) actually wants. */
export function toEpochMs(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

/** Coerces a playback-state row's `seq` (a `biginteger` — wire-serialised as either a number or a
 * string, same reason as `toEpochMs`'s neighbouring doc comment) into a comparable `number`.
 * `-1` for anything missing/unusable, so a comparison against it always loses — shared by every
 * ordering guard in `usePlaybackState.ts` (`refresh()`, the BroadcastChannel/SSE paths, and now
 * `patch()` itself — see `isFreshEnoughToApply`'s own doc comment) instead of each keeping its own
 * copy. */
export function seqOf(value: { seq: number | string } | null | undefined): number {
  if (!value) return -1;
  const n = Number(value.seq);
  return Number.isFinite(n) ? n : -1;
}

/**
 * R9-P2-C fix (round 9, live-Chrome critic): `usePlaybackState.ts`'s `patch()` used to call
 * `setState(updated)` with the HTTP response applied unconditionally — every OTHER path into this
 * hook (`refresh()`, the BroadcastChannel 'state' handler, the SSE 'playback' handler) already
 * guards on `seqOf`, but `patch()` alone did not. Two `patch()` calls fired back-to-back with no
 * shared ordering (e.g. the stall watchdog's error patch immediately followed by `advanceToNext`'s
 * mode-change patch, both in `usePlaybackEngine.ts`) race independent HTTP round trips with no
 * guarantee their RESPONSES resolve in the same order the requests were sent — critic reproduced
 * the Stage's error hold card silently not appearing this way: the later write's response landed
 * first, then the earlier write's now-stale response landed after it and reapplied the OLDER
 * `errorCode`/`mode` right over the newer one, with nothing to say the second `setState` had just
 * gone backwards. `>=`, not `>`: a call's own response, echoing the seq it just produced, must
 * still be allowed to apply.
 */
export function isFreshEnoughToApply(candidate: { seq: number | string }, current: { seq: number | string } | null | undefined): boolean {
  return seqOf(candidate) >= seqOf(current);
}

export type EndOfTrackDecision = 'advance' | 'close-out';

/**
 * R9-P1-B fix (round 9, live-Chrome critic): "Reached the end of the track" with nothing queued
 * next used to leave the just-finished queue entry `status:'playing'`/`endedAt:null` forever
 * (confirmed live on 3 separate entries in one session) — `usePlaybackEngine.ts`'s `advanceToNext`
 * short-circuited on `!nextSong` BEFORE ever invoking the shared `onAdvance` sequencing, so
 * `OperatorConsole.tsx`'s `advance()` (the only code that closes out queue-entry statuses) never
 * ran. `onAdvance` is now invoked unconditionally on end-of-track; this is the one-line decision
 * the caller makes with whatever it has for "the next queue entry" once it gets there — pulled out
 * as its own pure function so the three-way "advance / close out / nothing to do at all" table
 * (see `OperatorConsole.tsx`'s `handleEndOfTrack`) has exactly one place the boolean lives.
 */
export function decideEndOfTrackDecision(hasNext: boolean): EndOfTrackDecision {
  return hasNext ? 'advance' : 'close-out';
}

/**
 * The playback-state patch for `decideEndOfTrackDecision`'s `'close-out'` branch: `queueEntry`
 * clears (the "playing" row is gone — the caller separately writes that queue entry's own
 * `status:'done'`, which stamps `endedAt`) but `song` is deliberately left untouched so the Stage
 * can still show whatever "nothing playing next" treatment it has for the just-finished song,
 * rather than snapping straight to a blank/attract screen.
 *
 * R10-P2-2 fix (round 10, live-Chrome critic): this used to leave `mode` exactly as it was found
 * (almost always still `'playing'`, since nothing else changes `mode` at end-of-track) — confirmed
 * live as the idle stage showing the just-finished song's last frame plus a frozen lyrics readout
 * ("Aurora Skyline 0:09/0:14") forever, because `stageScene.ts`'s `pickStageScene` maps
 * `mode:'playing'` + `hasSong` (still true — `song` is deliberately kept) straight to the `'stage'`
 * scene, never `'hold'`/`'attract'`. `PlaybackMode` has no dedicated `'ended'` value (schema.json
 * change out of this wave's web/-only reach), so `'idle'` is the closest honest value — and it is
 * exactly what routes `pickStageScene` to `'hold'` (an upcoming entry queued) or `'attract'`
 * (nothing queued) regardless of `hasSong`, which is the whole point: once ended, `hasSong` no
 * longer decides anything. `positionMs` is stamped to the element's own duration (the caller passes
 * `engine.durationMs`) rather than left at whatever stale mid-song value the last periodic
 * re-anchor wrote, so a freeze-framed Stage reading a leftover `0:09/0:14` cannot happen even in
 * the instant before the scene swap takes effect.
 */
export function endOfRotationPatch(durationMs: number): { isPlaying: false; queueEntry: null; mode: 'idle'; positionMs: number } {
  return { isPlaying: false, queueEntry: null, mode: 'idle', positionMs: Number.isFinite(durationMs) ? Math.max(0, Math.round(durationMs)) : 0 };
}

/**
 * R10-P1-1 fix (round 10, live-Chrome critic): every playback-state write used to stamp
 * `positionAtServerMs` off the LOCAL clock (`withPositionStamp`'s default `Date.now()`), while
 * `extrapolatePositionMs` (clock.ts) reads it as SERVER time and corrects the caller's own `nowMs`
 * by `clockOffsetMs` before comparing — a systematic bias between the two clocks (critic measured
 * -1.1s against the server's own `updatedAt`) that never showed up in same-machine testing (where
 * `clockOffsetMs` happens to be ~0) but skews real cross-device sync. `usePlaybackState.ts`'s
 * `patch()` is the one place every write funnels through (`withPositionStamp`'s own doc comment),
 * so converting to server time there, via this pure conversion, fixes every call site at once
 * rather than auditing each one. Pulled out as its own function purely so the arithmetic is
 * testable without mocking `Date.now()`/the network round trip `clockOffsetMs` itself depends on.
 */
export function serverNowMs(localNowMs: number, clockOffsetMs: number): number {
  // R11-P2-4 fix (round 11, live-Chrome critic): `clockOffsetMs` (`computeClockOffsetMs`, clock.ts)
  // is a round-trip-midpoint AVERAGE and is not generally an integer — critic caught a stamp of
  // `1788421521975.5` reach the wire this way. Rounded here, at the source, rather than trusting
  // every caller (or `withPositionStamp`'s own default-`Date.now()` path, which never sees this
  // function at all) to remember to.
  return Math.round(localNowMs + clockOffsetMs);
}

/** The `StageSyncPayload` (stageChannel.ts) for a given `playback-state` row — the exact set of
 * fields `usePlaybackState.ts`'s `patch()` already posted after every successful write. Pulled out
 * so the round-10 SSE-frame rebroadcast (see `shouldRebroadcastFrame`'s own doc comment) posts the
 * IDENTICAL shape through the IDENTICAL code path instead of hand-duplicating this field list a
 * second time somewhere a future edit to one copy could silently miss the other. */
export function toStageSyncPayload(state: PlaybackState): StageSyncPayload {
  return {
    mode: state.mode,
    isPlaying: state.isPlaying,
    positionMs: state.positionMs,
    positionAtServerMs: toEpochMs(state.positionAtServerMs),
    keyShift: state.keyShift,
    tempoPct: state.tempoPct,
    vocalChannel: state.vocalChannel,
    volume: state.volume,
    lyricOffsetMs: state.lyricOffsetMs,
    seq: seqOf(state),
    songDocumentId: state.song?.documentId ?? null,
    errorCode: state.errorCode,
    errorMessage: state.errorMessage,
  };
}

/**
 * R10-P1-3 fix (round 10, live-Chrome critic): the Stage window never opens its own SSE connection
 * (`App.tsx`'s realtime client is console-only by design) and, before this fix, had no way to learn
 * about a playback-state change made by anything other than THIS browser's own cockpit `patch()`
 * call — critic reproduced an API-driven `errorCode` change while paused/ended never reaching the
 * Stage in 80s, only a full reload picked it up. The cockpit's own SSE connection (Path 2 in
 * `usePlaybackState.ts`) now rebroadcasts every frame it receives over the same BroadcastChannel
 * `patch()` already posts to, seq-guarded so the SSE echo of a write THIS cockpit just made via
 * `patch()` (which already posted once) is never posted a second time under a different code path.
 * Strictly greater, not `>=`, for exactly that reason — `isFreshEnoughToApply`'s own `>=` answers a
 * different question ("should I apply this to local state") from this one ("have I already told the
 * Stage about this exact seq").
 */
export function shouldRebroadcastFrame(frameSeq: number | string, lastBroadcastSeq: number | string | null): boolean {
  const last = lastBroadcastSeq == null ? -1 : seqOf({ seq: lastBroadcastSeq });
  return seqOf({ seq: frameSeq }) > last;
}

/**
 * R10-P1-3 fix (round 10, live-Chrome critic): the Stage's primary sync path (BroadcastChannel) is
 * same-machine-only by construction — a popup opened under a different OS profile, a genuinely
 * second device acting as the Stage, or simply a channel message dropped for any reason all leave
 * it with no live signal at all otherwise. `usePlaybackState.ts` tracks the wall-clock time of the
 * last BroadcastChannel 'state' message it saw (regardless of realm — both the cockpit and the
 * Stage run this same hook) and polls `GET /api/playback/state` once that goes quiet for too long.
 * Pulled out as its own pure predicate so the "how long is too long" threshold is one number a test
 * can pin down, not a magic literal buried inside a `setInterval` callback.
 */
export function shouldPollForFallback(lastBroadcastAtMs: number, nowMs: number, thresholdMs: number = 10_000): boolean {
  return nowMs - lastBroadcastAtMs >= thresholdMs;
}

/**
 * R11-P2-1 fix (round 11, live-Chrome critic): every play/resume/advance used to issue TWO
 * `POST /api/playback/state` within ~10ms of each other — `usePlaybackEngine.ts`'s `onPlay`
 * (the DOM `play` event, optimistic `{isPlaying:true, mode:'playing'}`) immediately followed by
 * `togglePlay()`'s own complete, position/error-stamped patch once that SAME `.play()` call
 * resolves, because calling `.play()` is exactly what fires the `play` event in the first place.
 * Pulled out as its own pure predicate (rather than inlining the check at `onPlay`'s one call
 * site) so the decision — "is a togglePlay-initiated play already in flight and about to write
 * the complete version itself" — is directly testable without mounting the hook. `onPlay` still
 * needs to run for a `play` event that did NOT originate from `togglePlay()` (the gapless
 * crossfade's own `back.el.play()`, which has no other patch coming) — hence a predicate, not an
 * unconditional skip.
 */
export function shouldWriteOnPlayPatch(togglePlayInFlight: boolean): boolean {
  return !togglePlayInFlight;
}

/**
 * MVP-345. `mode` carries two different kinds of fact at once. `'playing'`/`'paused'` describe the
 * TRANSPORT; `'filler'` describes what the SESSION is doing — break music is running, outside the
 * rotation, with no queue entry and its own open `play-history` row. Both of `usePlaybackEngine`'s
 * play-path patches used to stamp `mode:'playing'` unconditionally, so the moment MVP-343 made break
 * music actually start, `togglePlay()`'s own completing patch wrote `'playing'` straight over the
 * `'filler'` the server had set a beat earlier.
 *
 * Confirmed live on the deployed build: the track played, and the app stopped calling it break
 * music. The Stage badge vanished, the panel's Stop button turned back into Start (leaving no way
 * to stop the bus, and a second click ready to open a second filler history row), and
 * `cueFromRotationMeta`'s "stop the bus before cueing a singer" step — which keys on the same mode —
 * would no longer fire, stranding that history row open for the rest of the night.
 *
 * `isPlaying` already carries "is sound coming out", which is the only thing the play patch actually
 * learned. So a play patch keeps `'filler'` when that is what the session is already doing, and says
 * `'playing'` for everything else. A pure function rather than an inline ternary at the two call
 * sites because the hook cannot be mounted under this repo's `environment:'node'` vitest.
 *
 * The pause path (`mode:'paused'`) is deliberately NOT covered here — see MVP-345's own notes: it
 * has the same shape but needs a separate decision about what the Stage should show for paused break
 * music, and guessing at it inside a bug fix would be inventing behaviour nobody asked for.
 */
export function playingModeFor(currentMode: PlaybackState['mode'] | undefined): 'playing' | 'filler' {
  return currentMode === 'filler' ? 'filler' : 'playing';
}

/**
 * R11-P1-2 fix (round 11, live-Chrome critic): the pure decision behind `usePlaybackState.ts`'s
 * role-gated poll-fallback effect — see that hook's own doc comment on `role` for the ~24 GET/min
 * forever bug this closes. Only the Stage window (no SSE connection of its own) ever needs the
 * HTTP poll fallback; every console-side consumer is consolidated onto one instance
 * (`PlaybackStateProvider`) that always has a live SSE connection and must never poll. Pulled out
 * as its own predicate so the role gate is directly testable without mounting the hook.
 */
export function shouldRunPollFallback(role: 'console' | 'stage'): boolean {
  return role === 'stage';
}

/**
 * R12-P1-A fix (round 12, live-Chrome critic): `usePlaybackEngine.ts`'s `onPause` used to write
 * `{isPlaying:false, mode:'paused', positionMs}` unconditionally (after its `justErrored`/
 * `tearingDown`/`hasPlayed` guards) — including for the `pause` event a track's own natural
 * end-of-track fires on the element (`el.ended === true`). Critic caught that write landing
 * 69-81ms AFTER `onEnded`'s own `advanceToNext` had already written the honest `mode:'idle'`
 * close-out, re-attaching the just-finished song to a `mode:'paused'` row the Stage then froze on
 * forever (the last video frame + lyrics, with no way to ever leave that state). The end-of-track
 * path (`onEnded` -> `advanceToNext`/`OperatorConsole.tsx`'s `closeOutNoNext`) is the ONE owner of
 * what playback-state should say once a track ends; `onPause` must step aside whenever the pause it
 * is reacting to is that same ending, not a KJ's own pause click. `togglePlayInFlight` mirrors
 * `shouldWriteOnPlayPatch`'s own reasoning in the opposite direction: a pause `togglePlay()` itself
 * is about to fully own the write for (there is no such path today — `togglePlay()`'s pause branch
 * calls `el.pause()` and returns without setting the flag — but the guard costs nothing and closes
 * the same class of double-write if that ever changes).
 *
 * MVP-11 #1 fix: `stopRequested` closes the same class of race for a real transport Stop —
 * `OperatorConsole.tsx`'s `handleStop` used to call `togglePlay()` (pausing the element) THEN patch
 * `mode:'idle'` itself; the DOM `pause` event this induces reaches `onPause` asynchronously and,
 * with none of the other guards above applicable, used to win the race and overwrite the idle patch
 * with `mode:'paused'` — the Stage froze on a video frame again. `usePlaybackEngine.ts`'s `stop()`
 * sets a ref before pausing and `onPause` consumes it here; the idle patch is `stop()`'s own to make.
 */
export function shouldWriteOnPause(params: {
  ended: boolean;
  tearingDown: boolean;
  hasPlayed: boolean;
  togglePlayInFlight: boolean;
  stopRequested: boolean;
}): boolean {
  if (params.ended) return false; // onEnded's own advanceToNext/closeOutNoNext owns this write
  if (params.tearingDown) return false; // the pagehide/beforeunload handler already anchored position honestly
  if (!params.hasPlayed) return false; // browser teardown/reset noise around a fresh mount/reload, not a real pause
  if (params.togglePlayInFlight) return false; // togglePlay()'s own complete patch is about to write this
  if (params.stopRequested) return false; // stop()'s own idle patch owns this write — MVP-11 #1
  return true;
}

/**
 * R12-P2-A fix (round 12, live-Chrome critic): the Stage's HTTP poll fallback
 * (`shouldPollForFallback` above) fires whenever no BroadcastChannel 'state' frame has arrived in
 * 10s — which is exactly what happens whenever the cockpit is open but genuinely PAUSED (a paused
 * session has nothing new to broadcast). Critic measured 12 polls/min in that state. The console
 * role now posts a lightweight `{type:'heartbeat'}` on the same channel every ~4s purely to keep
 * that "still alive" clock fresh — this is the one-line decision behind which role does the
 * posting, pulled out so it is directly testable without mounting the hook (mirrors
 * `shouldRunPollFallback`'s own role-gate one function up).
 */
export function shouldPostHeartbeat(role: 'console' | 'stage'): boolean {
  return role === 'console';
}
